<script setup lang="ts">
import { useCurrentUser } from "@/api/current-user";
import { useRestoreUser } from "@/api/user-query";
import { useRoleLabel } from "@/composable/role-label";
import { useSnackbarStore } from "@/stores";
import { mdiAccountCircle, mdiCogOutline, mdiRestore } from "@mdi/js";
import { Role, UserSummary } from "@soft-db/monitoring-api-client";
import { Badge, Button, confirm, Icon, LinkButton, makeColumnHelper, Tooltip } from "@soft-db/ui";
import { RouteLocationRaw } from "vue-router";
import AppAvatar from "../common/AppAvatar.vue";
import AppDataTable from "../common/AppDataTable.vue";

type UserRowData = {
	id: string;
	avatar: string;
	name: string;
	role: Role;
	archived: boolean;
};

const { t } = useI18n();

const props = defineProps<{
	users: UserSummary[];
	settingLocation: (user: UserRowData) => RouteLocationRaw;
}>();

const userRows = computed<UserRowData[]>(() => {
	if (!props.users) return [];

	return props.users
		.map((user) => ({
			id: user.id,
			avatar: user.avatarUrl,
			name: user.name,
			role: user.role,
			archived: user.archived,
		}))
		.sort((a, b) => {
			// Sort archived last
			if (a.archived === b.archived) {
				return 0;
			} else if (a.archived) {
				return 1;
			} else return -1;
		});
});

const { currentUser, hasRole } = useCurrentUser();
function isSelf(id: string) {
	return currentUser.value?.id === id;
}

const roleLabel = useRoleLabel();

const snackbar = useSnackbarStore();
const userRestore = useRestoreUser();
async function restoreUser(user: UserRowData) {
	if (await confirm.yesNo(t("user.restoreConfirm", { n: user.name }))) {
		await userRestore.mutateAsync(user.id).then(snackbar.openOnSaved).catch(snackbar.openOnError);
	}
}

const { Column } = makeColumnHelper<UserRowData>();
</script>

<template>
	<AppDataTable layout="contained" :data="userRows" :pagination="{ pageSize: 10, pageIndex: 0 }">
		<template #action>
			<slot name="action" />
		</template>

		<template #columns>
			<Column
				v-slot="{ row }"
				field="name"
				:header="t('common.name')"
				enableSorting
				enableGlobalFilter
			>
				<div class="flex flex-row items-center gap-2">
					<AppAvatar
						class="size-8 rounded-full"
						:class="row.original.archived ? 'text-muted' : ''"
						table
						:icon="mdiAccountCircle"
						:src="row.original.avatar"
					/>
					<span :class="row.original.archived ? 'text-muted' : ''">{{ row.original.name }}</span>
					<Badge v-if="isSelf(row.original.id)" class="ml-2" size="xs" color="primary">
						{{ t("common.you") }}
					</Badge>
				</div>
			</Column>
			<Column
				v-slot="{ row }"
				field="role"
				:header="t('space.access')"
				enableSorting
				enableGlobalFilter
			>
				<span :class="row.original.archived ? 'text-muted' : ''">{{
					roleLabel(row.original.role)
				}}</span>
			</Column>
			<Column v-slot="{ row }" shrink>
				<div v-if="row.original.archived" class="flex gap-2">
					<Tooltip :tooltip="t('user.restore')">
						<Button
							variant="soft"
							color="warning"
							size="sm"
							@click="() => restoreUser(row.original)"
						>
							<Icon :d="mdiRestore" />
						</Button>
					</Tooltip>
				</div>
				<LinkButton
					v-else-if="!isSelf(row.original.id) && hasRole(row.original.role)"
					class="float-right"
					variant="soft"
					size="sm"
					:to="settingLocation(row.original)"
				>
					<Icon :d="mdiCogOutline" />
				</LinkButton>
			</Column>
		</template>
	</AppDataTable>
</template>
